"use client";
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Card, Link } from "@cappelendamm/design-system/react";
import { HeaderQueryResult } from "@/cms/sanity.types";
import classNames from "classnames";
import { usePathname } from "next/navigation";
import NextLink from "next/link";
import { MENU_UNDERLINE_CLASS } from "./utils";

type Submenu = Extract<
  NonNullable<NonNullable<HeaderQueryResult>["linkGroup"]>[number],
  { _type: "submenuLinks" }
>;

type SubmenuProps = Pick<Submenu, "_key" | "title"> & {
  children: ReactNode;
  titleHref?: string | null;
  titleExternal?: boolean;
};

export default function Submenu({
  _key,
  title,
  children,
  titleHref,
  titleExternal,
}: SubmenuProps) {
  const pathName = usePathname();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [pathName]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  function handleMouseEnter() {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    open();
  }

  function handleMouseLeave() {
    closeTimeoutRef.current = setTimeout(() => {
      close();
    }, 150);
  }

  function handleFocus() {
    open();
  }

  function handleBlur(e: React.FocusEvent) {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(e.relatedTarget as Node)
    ) {
      close();
    }
  }

  const triggerClassName = classNames("p-0 min-h-fit", MENU_UNDERLINE_CLASS);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {titleHref ? (
        <Link
          as={titleExternal ? "a" : NextLink}
          href={titleHref}
          target={titleExternal ? "_blank" : undefined}
          rel={titleExternal ? "noopener noreferrer" : undefined}
          variant="menu"
          className={classNames(triggerClassName, {
            "underline decoration-[var(--cvr-submenu-background,#333333)] decoration-2 underline-offset-2":
              isOpen,
          })}
        >
          {title}
        </Link>
      ) : (
        <Link
          as="button"
          variant="menu"
          onClick={() => setIsOpen((prev) => !prev)}
          className={classNames(triggerClassName, {
            "underline decoration-[var(--cvr-submenu-background,#333333)] decoration-2 underline-offset-2":
              isOpen,
          })}
        >
          {title}
        </Link>
      )}
      <Card
        as="ul"
        className={classNames(
          "absolute top-full left-0 z-20 mt-3 gap-3 rounded-tl-none rounded-tr-xl rounded-br-xl rounded-bl-xl border-none shadow-none",
          "before:absolute before:-top-2 before:left-3 before:size-0 before:border-x-6 before:border-b-8 before:border-x-transparent before:border-b-[var(--cvr-submenu-background,#333333)]",
          "p-6 *:justify-start *:justify-self-start *:p-0",
          isOpen ? "grid" : "hidden",
        )}
        style={
          {
            "--cvr-card-default": "var(--cvr-submenu-background, #333333)",
            color: "var(--cvr-submenu-contrast, #ffffff)",
          } as CSSProperties
        }
      >
        {children}
      </Card>
    </div>
  );
}
